import React, { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { PlxsButton, PlxsToggleButton } from "@plexlogic/plexus-ui";
import apiService from "../../services/api";
import DrawList, { DrawListHandle } from "./DrawList";
import DeleteConfirmationPopover from "../DeleteConfirmationPopover";
import WizardRefreshButton from "../WizardRefreshButton";
import { toSentenceCase } from "../../utils/stringUtils";
import { useNotification } from "../../contexts/NotificationContext";
import { resetWizardForNewDraw } from "../../services/wizardStorage";
import "./UserDashboard.css";

type FilterType = "all" | "scheduled" | "completed";

const UserDashboard: React.FC = () => {
  const history = useHistory();
  const { showNotification } = useNotification();
  const [activeFilter, setActiveFilter] = useState<FilterType>("all");
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
  const [deleteDrawId, setDeleteDrawId] = useState<string | null>(null);
  const [deleteDrawStatus, setDeleteDrawStatus] = useState<string | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Refs to DrawList components for selective updates
  const scheduledListRef = useRef<DrawListHandle>(null);
  const completedListRef = useRef<DrawListHandle>(null);
  const filteredListRef = useRef<DrawListHandle>(null);

  // Cleanup: Clear anchor element reference on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      setAnchorElement(null);
      setDeletePopoverOpen(false);
      setDeleteDrawId(null);
      setDeleteDrawStatus(null);
    };
  }, []);

  const handleCreateNew = useCallback(() => {
    resetWizardForNewDraw();
    history.push("/draws/wizard");
  }, [history]);

  const handleDeleteDraw = useCallback(
    (drawId: string, status: string, event?: React.MouseEvent<HTMLElement>) => {
      if (event?.currentTarget) {
        const element = event.currentTarget;
        // Store draw ID and status for later selective refetch
        setDeleteDrawId(drawId);
        setDeleteDrawStatus(status);
        setAnchorElement(element);
        // Use requestAnimationFrame to ensure anchor is set before opening
        requestAnimationFrame(() => {
          setDeletePopoverOpen(true);
        });
      }
    },
    []
  );

  const handleCancelDelete = useCallback(() => {
    if (isDeleting) return; // Prevent closing while deleting
    setDeletePopoverOpen(false);
    setDeleteDrawId(null);
    setDeleteDrawStatus(null);
    setAnchorElement(null);
  }, [isDeleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteDrawId || isDeleting) return;

    setIsDeleting(true);

    // Close popover FIRST before any DOM changes
    setDeletePopoverOpen(false);

    // Small delay to allow popover to close gracefully before optimistic delete
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Optimistically remove from the appropriate list
    const status = deleteDrawStatus?.toLowerCase();
    if (activeFilter === "all") {
      if (status === "scheduled") {
        scheduledListRef.current?.removeDrawOptimistically(deleteDrawId);
      } else if (status === "completed") {
        completedListRef.current?.removeDrawOptimistically(deleteDrawId);
      }
      // Note: draft draws are no longer shown in the dashboard
    } else {
      filteredListRef.current?.removeDrawOptimistically(deleteDrawId);
    }

    try {
      await apiService.deleteDraw(deleteDrawId);
      // Success: Clear state
      setDeleteDrawId(null);
      setDeleteDrawStatus(null);
      setAnchorElement(null);

      // Show success notification
      showNotification("Draw archived successfully", "", "positive");
    } catch (err) {
      // Error: Refetch the affected list to restore correct state
      if (activeFilter === "all") {
        if (status === "scheduled") {
          scheduledListRef.current?.refetch();
        } else if (status === "completed") {
          completedListRef.current?.refetch();
        }
        // Note: draft draws are no longer shown in the dashboard
      } else {
        filteredListRef.current?.refetch();
      }
      setDeleteDrawId(null);
      setDeleteDrawStatus(null);
      setAnchorElement(null);
    } finally {
      setIsDeleting(false);
    }
  }, [
    deleteDrawId,
    deleteDrawStatus,
    activeFilter,
    isDeleting,
    showNotification,
  ]);

  const handleViewActivity = useCallback(
    (drawId: string) => {
      // Navigate to DrawDetailComponent where activity feed is displayed
      history.push(`/draws/${drawId}`);
    },
    [history]
  );

  const handleFilterChange = useCallback((value: string) => {
    setActiveFilter(value as FilterType);
  }, []);

  return (
    <div className="user-dashboard" data-testid="draw-dashboard-page">
      <div className="user-dashboard-header">
        <div className="user-dashboard-title">
          <h1>{toSentenceCase("dashboard")}</h1>
        </div>

        <div style={{ display: "flex", gap: "var(--plexus-space-sm)" }}>
          <WizardRefreshButton
            iconOnly
            onClick={() => setRefetchTrigger((prev) => prev + 1)}
            dataTestId="draw-dashboard-refresh-button"
          />
          <PlxsButton
            variant="primary"
            functionalIcon="add"
            label={toSentenceCase("create new draw")}
            onClick={handleCreateNew}
            tone="positive"
            dataTestId="draw-dashboard-create-button"
          />
        </div>
      </div>

      <div className="user-dashboard-filters">
        <PlxsToggleButton
          buttons={[
            {
              label: toSentenceCase("all draws"),
              dataTestId: "draw-dashboard-filter-all-button",
              value: "all",
              icon: "grid",
            },
            {
              label: toSentenceCase("scheduled"),
              dataTestId: "draw-dashboard-filter-scheduled-button",
              value: "scheduled",
              icon: "arrowClock",
            },
            {
              label: toSentenceCase("completed"),
              dataTestId: "draw-dashboard-filter-completed-button",
              value: "completed",
              icon: "check",
            },
          ]}
          onChange={handleFilterChange}
          value={activeFilter}
        />
      </div>

      <div className="user-dashboard-content">
        {activeFilter === "all" ? (
          <>
            <DrawList
              ref={scheduledListRef}
              title={toSentenceCase("scheduled")}
              statusFilter="scheduled"
              onDeleteDraw={handleDeleteDraw}
              onViewActivity={handleViewActivity}
              onCreateNew={handleCreateNew}
              emptyMessage="No scheduled draws"
              refetchTrigger={refetchTrigger}
            />
            <DrawList
              ref={completedListRef}
              title={toSentenceCase("completed")}
              statusFilter="completed"
              onDeleteDraw={handleDeleteDraw}
              onViewActivity={handleViewActivity}
              onCreateNew={handleCreateNew}
              emptyMessage="No completed draws found"
              refetchTrigger={refetchTrigger}
            />
          </>
        ) : (
          <DrawList
            ref={filteredListRef}
            title={toSentenceCase(
              activeFilter === "scheduled"
                ? "scheduled draws"
                : "completed draws"
            )}
            statusFilter={activeFilter}
            onDeleteDraw={handleDeleteDraw}
            onViewActivity={handleViewActivity}
            onCreateNew={handleCreateNew}
            emptyMessage={`No ${activeFilter} draws found`}
            refetchTrigger={refetchTrigger}
          />
        )}
      </div>

      <DeleteConfirmationPopover
        anchorElement={anchorElement}
        isOpen={deletePopoverOpen}
        itemName={"draw"}
        message="This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDeleting={isDeleting}
        dataTestId="delete-draw-confirmation"
      />
    </div>
  );
};

export default UserDashboard;
